import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirebaseApp } from "../analytics/firebase-app.js";
import { signLeaderboardSubmit } from "./submit-signature.js";
import {
  PLAYER_NAME_STORAGE_KEY,
  LEADERBOARD_MAX_NAME_LENGTH,
  LEADERBOARD_MAX_SCORE,
  LEADERBOARD_DEFAULT_LIMIT,
  normalizeLeaderboardScore,
  sanitizeLeaderboardName,
  getPlayerDisplayName,
  getPlayerId,
  ensurePlayerId,
  ensureLeaderboardAuth,
  extractUidFromEntryId,
  normalizeLeaderboardEntry,
  mergeOwnEntry,
  parseLeaderboardSnapshot,
  normalizeLeaderboardRows,
  isBrowser,
  isTestMode,
} from "./helpers.js";

export { LEADERBOARD_TOP_PATH } from "./constants.js";
export { LEADERBOARD_SCORES_PATH } from "./constants.js";
export { LEADERBOARD_TOP_SIZE } from "./constants.js";
export {
  PLAYER_NAME_STORAGE_KEY,
  LEADERBOARD_MAX_NAME_LENGTH,
  LEADERBOARD_MAX_SCORE,
  LEADERBOARD_DEFAULT_LIMIT,
  normalizeLeaderboardScore,
  sanitizeLeaderboardName,
  getPlayerDisplayName,
  getPlayerId,
  ensurePlayerId,
  ensureLeaderboardAuth,
  extractUidFromEntryId,
  normalizeLeaderboardEntry,
  mergeOwnEntry,
  parseLeaderboardSnapshot,
  normalizeLeaderboardRows,
};

/**
 * @param {unknown} data
 * @returns {Array<{ id: string, name: string, score: number, uid: string, createdAt: number }> | null}
 */
function rowsFromCallableData(data) {
  if (!data || typeof data !== "object" || !Array.isArray(/** @type {{ rows?: unknown }} */ (data).rows)) {
    return null;
  }
  return normalizeLeaderboardRows(/** @type {{ rows: unknown[] }} */ (data).rows);
}

/**
 * @param {{ name: string, score: number, runIndex: number, playerId?: string }} params
 * @returns {Promise<false | { ok: boolean, rows: Array<{ id: string, name: string, score: number, uid: string, createdAt: number }> | null }>}
 */
export async function submitScore(params) {
  if (!isBrowser() || isTestMode()) return false;
  var score = normalizeLeaderboardScore(params && params.score);
  if (score <= 0) return false;

  var runIndex = Math.max(0, Math.floor(Number(params && params.runIndex) || 0));
  var name = sanitizeLeaderboardName(params && params.name);
  var playerId = (params && params.playerId) || getPlayerId();
  if (!playerId) return false;

  try {
    var app = getFirebaseApp();
    if (!app) return false;
    var ts = Date.now();
    var sig = await signLeaderboardSubmit({
      playerId: playerId,
      runIndex: runIndex,
      score: score,
      ts: ts,
    });
    var functions = getFunctions(app);
    var callable = httpsCallable(functions, "submitScore");
    var response = await callable({
      playerId: playerId,
      runIndex: runIndex,
      name: name,
      score: score,
      ts: ts,
      sig: sig,
    });
    var data = response && response.data;
    return {
      ok: !data || /** @type {{ ok?: unknown }} */ (data).ok !== false,
      rows: rowsFromCallableData(data),
    };
  } catch (err) {
    console.warn("[leaderboard] submit failed:", err);
    return false;
  }
}

/**
 * Current board when this run is not submitted. Uses the same callable (`listOnly`)
 * so the client does not read RTDB. Shipped builds still read `leaderboard/top` themselves.
 * @param {number} [limit]
 * @returns {Promise<Array<{ id: string, name: string, score: number, uid: string, createdAt: number }>>}
 */
export async function fetchTopScores(limit) {
  if (!isBrowser() || isTestMode()) return [];
  var maxRows = limit != null ? Math.max(1, Math.floor(Number(limit) || LEADERBOARD_DEFAULT_LIMIT)) : LEADERBOARD_DEFAULT_LIMIT;

  try {
    var app = getFirebaseApp();
    if (!app) return [];
    var functions = getFunctions(app);
    var callable = httpsCallable(functions, "submitScore");
    var response = await callable({ listOnly: true });
    var rows = rowsFromCallableData(response && response.data) || [];
    return rows.slice(0, maxRows);
  } catch (err) {
    console.warn("[leaderboard] fetch failed:", err);
    return [];
  }
}

/**
 * @param {{ crazygamesUsername?: string | null, y8Nickname?: string | null, storage?: Storage | null }} [opts]
 * @returns {{
 *   getPlayerId: () => string | null,
 *   ensurePlayerId: typeof ensurePlayerId,
 *   ensureAuth: typeof ensurePlayerId,
 *   getDisplayName: () => string,
 *   submitScore: typeof submitScore,
 *   fetchTopScores: typeof fetchTopScores,
 *   mergeOwnEntry: typeof mergeOwnEntry,
 * }}
 */
export function createLeaderboardClient(opts) {
  var options = opts || {};
  return {
    getPlayerId: function () {
      return getPlayerId({ storage: options.storage });
    },
    ensurePlayerId: function () {
      return ensurePlayerId({ storage: options.storage });
    },
    ensureAuth: function () {
      return ensurePlayerId({ storage: options.storage });
    },
    getDisplayName: function () {
      return getPlayerDisplayName(options.crazygamesUsername, {
        storage: options.storage,
        y8Nickname: options.y8Nickname,
      });
    },
    submitScore: submitScore,
    fetchTopScores: fetchTopScores,
    mergeOwnEntry: mergeOwnEntry,
  };
}

/** Test helper — no-op; kept for test compatibility. */
export function resetLeaderboardAuthForTests() {}
